'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

const navLinks = [
  { label: 'Properties', href: '/properties' },
  { label: 'Services', href: '/services' },
  { label: 'About Us', href: '/about-us' },
  { label: 'Owner Services', href: '/owner-services' },
  { label: 'FAQ', href: '/faq' },
  { label: 'Contact', href: '/contact' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = usePathname();
  const isHome = pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 48);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const handleNavClick = () => setMenuOpen(false);

  // On non-home pages, always show the solid nav style
  const solidNav = !isHome || scrolled;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          solidNav
            ? 'nav-glass border-b border-border shadow-nav py-3'
            : 'bg-transparent py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 flex-shrink-0 group">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform duration-300">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 2L18 8V18H12V13H8V18H2V8L10 2Z" fill="#C8965A" />
                <path d="M10 2L18 8V18H12V13H8V18H2V8L10 2Z" fill="none" stroke="#FAF8F3" strokeWidth="0.5" strokeOpacity="0.4" />
              </svg>
            </div>
            <div className="flex flex-col leading-none">
              <span className={`font-bold text-sm tracking-tight transition-colors duration-300 ${solidNav ? 'text-primary' : 'text-white'}`}>
                Empire Venture
              </span>
              <span className={`text-[10px] font-mono tracking-widest uppercase transition-colors duration-300 ${solidNav ? 'text-muted-foreground' : 'text-white/70'}`}>
                Nepal
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks?.map((link) => (
              <Link
                key={link?.label}
                href={link?.href}
                className={`px-3 py-2 text-[13px] font-medium rounded-md transition-all duration-200 ${
                  pathname === link?.href
                    ? solidNav ? 'text-primary bg-muted' : 'text-white bg-white/15'
                    : solidNav
                    ? 'text-foreground hover:text-primary hover:bg-muted'
                    : 'text-white/85 hover:text-white hover:bg-white/10'
                }`}
              >
                {link?.label}
              </Link>
            ))}
          </nav>

          {/* Portal CTAs */}
          <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
            <Link
              href="/owner-portal"
              className={`px-4 py-2 text-[13px] font-semibold rounded-md transition-all duration-200 ${
                solidNav
                  ? 'text-primary border border-primary/30 hover:bg-primary hover:text-primary-foreground'
                  : 'text-white border border-white/30 hover:bg-white/15'
              }`}
            >
              Owner Portal
            </Link>
            <Link
              href="/tenant-portal"
              className="px-4 py-2 text-[13px] font-semibold rounded-md bg-accent text-foreground hover:bg-accent/90 transition-all duration-200 hover:shadow-md"
            >
              Tenant Portal
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className={`lg:hidden p-2 rounded-md transition-colors ${
              solidNav ? 'text-foreground hover:bg-muted' : 'text-white hover:bg-white/10'
            }`}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            <Icon name={menuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={22} />
          </button>
        </div>
      </header>
      {/* Mobile Menu Overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-40 bg-foreground/50 backdrop-blur-sm lg:hidden"
          onClick={() => setMenuOpen(false)}
        />
      )}
      {/* Mobile Menu Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-[300px] z-50 bg-card border-l border-border flex flex-col lg:hidden transition-transform duration-400 ease-out ${
          menuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <span className="font-bold text-foreground">Menu</span>
          <button
            onClick={() => setMenuOpen(false)}
            className="p-1.5 rounded-md hover:bg-muted text-muted-foreground"
            aria-label="Close menu"
          >
            <Icon name="XMarkIcon" size={20} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-1">
          {navLinks?.map((link) => (
            <Link
              key={link?.label}
              href={link?.href}
              onClick={handleNavClick}
              className={`flex items-center px-3 py-3 text-[15px] font-medium rounded-lg hover:bg-muted hover:text-primary transition-colors min-h-[44px] ${
                pathname === link?.href ? 'text-primary bg-muted' : 'text-foreground'
              }`}
            >
              {link?.label}
            </Link>
          ))}
        </nav>

        <div className="px-4 py-4 border-t border-border flex flex-col gap-2">
          <Link
            href="/owner-portal"
            onClick={handleNavClick}
            className="w-full text-center py-3 text-[14px] font-semibold text-primary border border-primary/30 rounded-lg hover:bg-primary hover:text-primary-foreground transition-all min-h-[44px] flex items-center justify-center"
          >
            Owner Portal
          </Link>
          <Link
            href="/tenant-portal"
            onClick={handleNavClick}
            className="w-full text-center py-3 text-[14px] font-semibold bg-accent text-foreground rounded-lg hover:bg-accent/90 transition-all min-h-[44px] flex items-center justify-center"
          >
            Tenant Portal
          </Link>
        </div>
      </div>
    </>
  );
}